import React from 'react';
import styled from 'styled-components';
import { BookOpen, Edit, Trash2 } from 'lucide-react';

interface SetCardProps {
  id: number;
  name: string;
  description?: string;
  totalCards: number;
  folderName?: string;
  createdBy?: number;
  creatorName?: string;
  currentUserId?: number;
  currentUserRole?: number;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const SetCard: React.FC<SetCardProps> = ({
  id,
  name,
  description,
  totalCards,
  folderName,
  createdBy,
  creatorName,
  currentUserId,
  currentUserRole,
  onSelect,
  onEdit,
  onDelete,
}) => {
  // Check if current user is the creator or admin
  const isOwner = currentUserId && createdBy && currentUserId === createdBy;
  const isAdmin = currentUserRole === 0;
  const canManage = isOwner || isAdmin;
  // Show creator info only for non-students (role !== 2)
  const shouldShowCreatorInfo = currentUserRole !== 2 && creatorName;

  return (
    <Card onClick={onSelect}>
      <IconBox>
        <BookOpen size={24} color="#3b82f6" />
      </IconBox>
      <Content>
        <Name>{name}</Name>
        {description && <Description>{description}</Description>}
        <Meta>
          <MetaItem>
            <span>ID: {id} &nbsp;|&nbsp; 📁 {folderName ? `Thuộc Folder: ${folderName}` : 'Không thuộc folder nào'}</span>
          </MetaItem>
          <MetaItem>
            📇 {totalCards} thẻ
          </MetaItem>
          {shouldShowCreatorInfo && (
            <MetaItem>
              👤 {creatorName} (user id: {createdBy})
            </MetaItem>
          )}
        </Meta>
      </Content>
      <Actions>
        {canManage && (
          <>
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Chỉnh sửa"
            >
              <Edit size={16} />
            </ActionButton>
            <ActionButton
              $danger
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              title="Xóa"
            >
              <Trash2 size={16} />
            </ActionButton>
          </>
        )}
      </Actions>
    </Card>
  );
};

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: white;
  border: 2px solid #e5e7eb;
  border-left: 4px solid #3b82f6;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #3b82f6;
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }
`;

const IconBox = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.h3`
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const Description = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #9ca3af;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${Card}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  padding: 8px;
  background: ${props => props.$danger ? '#fee2e2' : '#f3f4f6'};
  color: ${props => props.$danger ? '#dc2626' : '#6b7280'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$danger ? '#fecaca' : '#e5e7eb'};
    color: ${props => props.$danger ? '#b91c1c' : '#374151'};
  }
`;
